import { useEffect, useState, type ChangeEvent } from 'react';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { MainLayout } from '@/components/layout/MainLayout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { sambaApi } from '@/lib/pillarApi';
import {
    SambaLatestReleaseResponseSchema,
    type SambaArch,
    type SambaLatestReleaseQuery,
    type SambaLatestReleaseResponse,
    type SambaTrack,
    type SambaWindowsPackageRelease,
} from '@pillar-portal/api-types';

type SupportedOs = 'win' | 'mac';

type ClientDownloadDefaults = {
    os: SupportedOs;
    windowsArch: SambaArch;
};

const osSelectionSchema = z.union([z.literal('win'), z.literal('mac')]);
const windowsArchSelectionSchema = z.union([z.literal('x64'), z.literal('arm64')]);
const trackSelectionSchema = z.union([z.literal('stable'), z.literal('latest')]);
const navigatorSchema = z.object({
    platform: z.string(),
    userAgent: z.string(),
});

const buildReleaseCacheKey = (query: SambaLatestReleaseQuery): string => {
    return `${query.platform}:${query.arch}`;
};

const getReleaseQuery = (os: SupportedOs, windowsArch: SambaArch): SambaLatestReleaseQuery => {
    if (os === 'mac') {
        return { platform: 'mac', arch: 'arm64' };
    }
    return { platform: 'win', arch: windowsArch };
};

const detectClientDownloadDefaults = (): ClientDownloadDefaults => {
    const parsedNavigator = navigatorSchema.safeParse(globalThis.navigator);
    if (!parsedNavigator.success) {
        return { os: 'win', windowsArch: 'x64' };
    }

    const platform = parsedNavigator.data.platform.toLowerCase();
    const userAgent = parsedNavigator.data.userAgent.toLowerCase();
    const combinedPlatform = `${platform} ${userAgent}`;

    const os: SupportedOs = combinedPlatform.includes('mac') ? 'mac' : 'win';
    const windowsArch: SambaArch =
        combinedPlatform.includes('arm64') || combinedPlatform.includes('aarch64')
            ? 'arm64'
            : 'x64';

    return {
        os,
        windowsArch,
    };
};

const selectWindowsPackage = (
    response: SambaLatestReleaseResponse | null | undefined,
    track: SambaTrack,
    arch: SambaArch,
    packageType: 'installer' | 'portable'
): SambaWindowsPackageRelease | null => {
    const windowsTrack = response?.windowsTracks?.find((candidateTrack) => candidateTrack.track === track);
    if (!windowsTrack) {
        return null;
    }
    return (
        windowsTrack.packages.find(
            (candidatePackage) =>
                candidatePackage.arch === arch && candidatePackage.packageType === packageType
        ) ?? null
    );
};

function DownloadsContent() {
    const [clientDefaults, setClientDefaults] = useState<ClientDownloadDefaults>(() =>
        detectClientDownloadDefaults()
    );
    const [selectedOs, setSelectedOs] = useState<SupportedOs>(() => detectClientDownloadDefaults().os);
    const [selectedWindowsArch, setSelectedWindowsArch] = useState<SambaArch>(() =>
        detectClientDownloadDefaults().windowsArch
    );
    const [selectedTrack, setSelectedTrack] = useState<SambaTrack>('stable');
    const [releaseCache, setReleaseCache] = useState<Record<string, SambaLatestReleaseResponse>>({});
    const [isDownloadInProgress, setIsDownloadInProgress] = useState<boolean>(false);
    const [downloadErrorMessage, setDownloadErrorMessage] = useState<string | null>(null);

    useEffect(() => {
        const detectedDefaults = detectClientDownloadDefaults();
        setClientDefaults(detectedDefaults);
        setSelectedOs(detectedDefaults.os);
        setSelectedWindowsArch(detectedDefaults.windowsArch);
        setSelectedTrack('stable');
    }, []);

    const ensureReleaseLoaded = async (
        query: SambaLatestReleaseQuery
    ): Promise<SambaLatestReleaseResponse> => {
        const cacheKey = buildReleaseCacheKey(query);
        const cachedRelease = releaseCache[cacheKey];
        if (cachedRelease) {
            return cachedRelease;
        }

        const response = await sambaApi.getLatestRelease(query);
        const validatedResponse = SambaLatestReleaseResponseSchema.parse(response);
        setReleaseCache((currentCache) => ({
            ...currentCache,
            [cacheKey]: validatedResponse,
        }));
        return validatedResponse;
    };

    useEffect(() => {
        void ensureReleaseLoaded(getReleaseQuery(clientDefaults.os, clientDefaults.windowsArch)).catch(
            (error) => {
                setDownloadErrorMessage(
                    error instanceof Error
                        ? error.message
                        : 'Failed to load Anchorbase download information.'
                );
            }
        );
    }, [clientDefaults]);

    useEffect(() => {
        void ensureReleaseLoaded(getReleaseQuery(selectedOs, selectedWindowsArch)).catch((error) => {
            setDownloadErrorMessage(
                error instanceof Error
                    ? error.message
                    : 'Failed to load Anchorbase download information.'
            );
        });
    }, [selectedOs, selectedWindowsArch]);

    const openDownloadUrl = (downloadUrl: string): void => {
        const openedWindow: Window | null = window.open(downloadUrl, '_blank', 'noopener,noreferrer');
        if (openedWindow === null) {
            setDownloadErrorMessage(
                'Unable to open installer download. Please allow pop-ups and try again.'
            );
        }
    };

    const startDownload = async (resolvePackage: () => Promise<string>): Promise<void> => {
        setDownloadErrorMessage(null);
        setIsDownloadInProgress(true);
        try {
            openDownloadUrl(await resolvePackage());
        } catch (error) {
            setDownloadErrorMessage(
                error instanceof Error
                    ? error.message
                    : 'Failed to start Anchorbase installer download.'
            );
        } finally {
            setIsDownloadInProgress(false);
        }
    };

    const primaryReleaseResponse =
        releaseCache[buildReleaseCacheKey(getReleaseQuery(clientDefaults.os, clientDefaults.windowsArch))];
    const selectedReleaseResponse =
        releaseCache[buildReleaseCacheKey(getReleaseQuery(selectedOs, selectedWindowsArch))];

    const detectedWindowsInstaller = selectWindowsPackage(
        primaryReleaseResponse,
        'stable',
        clientDefaults.windowsArch,
        'installer'
    );
    const detectedWindowsPortable = selectWindowsPackage(
        primaryReleaseResponse,
        'stable',
        clientDefaults.windowsArch,
        'portable'
    );
    const selectedWindowsInstaller = selectWindowsPackage(
        selectedReleaseResponse,
        selectedTrack,
        selectedWindowsArch,
        'installer'
    );
    const latestWindowsTrack = selectedReleaseResponse?.windowsTracks?.find(
        (trackRelease) => trackRelease.track === 'latest'
    );

    const onSelectedOsChanged = (event: ChangeEvent<HTMLSelectElement>): void => {
        const nextOs = osSelectionSchema.parse(event.target.value);
        setSelectedOs(nextOs);
        if (nextOs === 'mac') {
            setSelectedTrack('stable');
        }
    };

    const onSelectedWindowsArchChanged = (event: ChangeEvent<HTMLSelectElement>): void => {
        setSelectedWindowsArch(windowsArchSelectionSchema.parse(event.target.value));
    };

    const onSelectedTrackChanged = (event: ChangeEvent<HTMLSelectElement>): void => {
        setSelectedTrack(trackSelectionSchema.parse(event.target.value));
    };

    const selectedDownloadButtonEnabled =
        selectedOs === 'mac' ? selectedTrack.length > 0 : selectedWindowsInstaller !== null;

    return (
        <div className="space-y-6">
            <h1 className="text-3xl font-bold">Downloads</h1>

            <Card className="max-w-3xl">
                <CardHeader>
                    <CardTitle>Recommended for This Device</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                        {clientDefaults.os === 'win'
                            ? `Detected Windows ${clientDefaults.windowsArch}. Stable builds are recommended for everyday use.`
                            : 'Detected macOS. Apple Silicon is the supported default download.'}
                    </p>

                    {clientDefaults.os === 'win' ? (
                        <>
                            <Button
                                type="button"
                                onClick={() =>
                                    void startDownload(async () => {
                                        const releaseResponse = await ensureReleaseLoaded(
                                            getReleaseQuery('win', clientDefaults.windowsArch)
                                        );
                                        const installerPackage = selectWindowsPackage(
                                            releaseResponse,
                                            'stable',
                                            clientDefaults.windowsArch,
                                            'installer'
                                        );
                                        if (!installerPackage) {
                                            throw new Error(
                                                `No stable Windows installer is available for ${clientDefaults.windowsArch}.`
                                            );
                                        }
                                        return installerPackage.downloadUrl;
                                    })
                                }
                                disabled={isDownloadInProgress || detectedWindowsInstaller === null}
                                className="w-fit"
                            >
                                {isDownloadInProgress ? 'Starting download...' : 'Anchorbase Installer'}
                            </Button>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() =>
                                    void startDownload(async () => {
                                        const releaseResponse = await ensureReleaseLoaded(
                                            getReleaseQuery('win', clientDefaults.windowsArch)
                                        );
                                        const portablePackage = selectWindowsPackage(
                                            releaseResponse,
                                            'stable',
                                            clientDefaults.windowsArch,
                                            'portable'
                                        );
                                        if (!portablePackage) {
                                            throw new Error(
                                                `No stable Windows standalone app is available for ${clientDefaults.windowsArch}.`
                                            );
                                        }
                                        return portablePackage.downloadUrl;
                                    })
                                }
                                disabled={isDownloadInProgress || detectedWindowsPortable === null}
                                className="w-fit"
                            >
                                Anchorbase standalone app
                            </Button>
                        </>
                    ) : (
                        <Button
                            type="button"
                            onClick={() =>
                                void startDownload(async () => {
                                    const releaseResponse = await ensureReleaseLoaded(
                                        getReleaseQuery('mac', 'arm64')
                                    );
                                    return releaseResponse.downloadUrl;
                                })
                            }
                            disabled={isDownloadInProgress || primaryReleaseResponse === undefined}
                            className="w-fit"
                        >
                            {isDownloadInProgress ? 'Starting download...' : 'Download .dmg'}
                        </Button>
                    )}
                </CardContent>
            </Card>

            <Card className="max-w-3xl">
                <CardHeader>
                    <CardTitle>Download a Different Version</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                    <label htmlFor="download-os" className="text-sm font-medium text-foreground">
                        1. Operating system
                    </label>
                    <select
                        id="download-os"
                        value={selectedOs}
                        onChange={onSelectedOsChanged}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        disabled={isDownloadInProgress}
                    >
                        <option value="win">Windows</option>
                        <option value="mac">Mac</option>
                    </select>

                    <label
                        htmlFor="download-arch"
                        className="text-sm font-medium text-foreground"
                    >
                        2. Windows architecture
                    </label>
                    <select
                        id="download-arch"
                        value={selectedWindowsArch}
                        onChange={onSelectedWindowsArchChanged}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        disabled={isDownloadInProgress || selectedOs !== 'win'}
                    >
                        <option value="x64">Windows x64</option>
                        <option value="arm64">Windows arm64</option>
                    </select>

                    <label
                        htmlFor="download-track"
                        className="text-sm font-medium text-foreground"
                    >
                        3. Track
                    </label>
                    <select
                        id="download-track"
                        value={selectedTrack}
                        onChange={onSelectedTrackChanged}
                        className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                        disabled={isDownloadInProgress || (selectedOs === 'win' && selectedWindowsArch.length === 0)}
                    >
                        <option value="stable">Stable</option>
                        <option value="latest">Latest</option>
                    </select>

                    <Button
                        type="button"
                        onClick={() =>
                            void startDownload(async () => {
                                const releaseResponse = await ensureReleaseLoaded(
                                    getReleaseQuery(selectedOs, selectedWindowsArch)
                                );
                                if (selectedOs === 'mac') {
                                    return releaseResponse.downloadUrl;
                                }
                                const installerPackage = selectWindowsPackage(
                                    releaseResponse,
                                    selectedTrack,
                                    selectedWindowsArch,
                                    'installer'
                                );
                                if (!installerPackage) {
                                    throw new Error(
                                        `No ${selectedTrack} Windows installer is available for ${selectedWindowsArch}.`
                                    );
                                }
                                return installerPackage.downloadUrl;
                            })
                        }
                        disabled={isDownloadInProgress || !selectedDownloadButtonEnabled}
                        className="w-fit"
                    >
                        {isDownloadInProgress
                            ? 'Starting download...'
                            : selectedOs === 'win'
                              ? '4. Download installer'
                              : '4. Download .dmg'}
                    </Button>

                    {selectedOs === 'win' && latestWindowsTrack ? (
                        <p className="text-sm text-muted-foreground">
                            Latest is built on {latestWindowsTrack.displayLabel} and is intended for
                            observation and regression testing.
                        </p>
                    ) : null}

                    {downloadErrorMessage !== null ? (
                        <p className="text-sm text-destructive">{downloadErrorMessage}</p>
                    ) : null}
                </CardContent>
            </Card>
        </div>
    );
}

export default function DownloadsPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <DownloadsContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
